import type { ClientAccessSettings } from "@repo/shared/types";
import { ArrowRight, Info } from "lucide-react";
import { useId, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/ui/link";
import { useConfirm } from "@/hooks/use-confirm";
import { apiErrorMessage } from "@/lib/api/_client";
import { useClientAccessSettings } from "@/lib/hooks/use-app-bootstrap";
import { useClientAccessImpact, useUpdateClientAccessSettings } from "@/lib/hooks/use-teams";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./client-access-card.i18n";
import { describeClientAccessConsequences } from "./client-access-consequences";
import { ClientAccessImpactPanel } from "./client-access-impact-panel";
import { ClientAccessSwitch } from "./client-access-switch";

const sameSettings = (a: ClientAccessSettings, b: ClientAccessSettings) =>
  a.privateEnabled === b.privateEnabled && a.teamAccessEnabled === b.teamAccessEnabled;

/**
 * QL-380 — Settings → Organization: the two independent client access switches.
 * Moving a switch only previews the resulting combination (impact counts from the
 * server); the change happens on "Save" after a confirm that repeats
 * freshly fetched counts. The page is Admin only.
 */
export function ClientAccessCard() {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const current = useClientAccessSettings();
  const [draft, setDraft] = useState<ClientAccessSettings | null>(null);
  const target = draft ?? current;
  const changing = !sameSettings(target, current);
  const impactId = useId();

  const {
    impact,
    isLoading: impactLoading,
    isFetching: impactFetching,
    isPlaceholderData: impactIsPrevious,
    refetch: refetchImpact,
  } = useClientAccessImpact(target);
  // Undefined while first loading or on error: the badge is simply omitted then. The team
  // count does not depend on the target, so the previous target's placeholder is fine here.
  const teamCount = impact?.teamCount;
  const noTeams = teamCount === 0;
  const update = useUpdateClientAccessSettings();
  const { confirm, ConfirmationDialog } = useConfirm();

  function set(patch: Partial<ClientAccessSettings>) {
    const next = { ...target, ...patch };
    // The impact query is keyed by the target settings, so this fetches its counts.
    setDraft(sameSettings(next, current) ? null : next);
  }

  async function save() {
    const saving = target;
    // The confirm repeats the numbers, so it must not quote a stale cache.
    const fresh = await refetchImpact();
    const counts = fresh.isError ? null : (fresh.data ?? null);
    const consequences = counts
      ? describeClientAccessConsequences(saving, counts, userLanguage).map((c) => c.text)
      : [t.confirmFallback];
    const summary = t.confirmSettings({
      private: saving.privateEnabled ? t.on : t.off,
      team: saving.teamAccessEnabled ? t.on : t.off,
    });
    const ok = await confirm({
      title: t.confirmTitle,
      description: [summary, ...consequences].join(" "),
      confirmText: t.confirm,
    });
    if (!ok) return;
    update.mutate(saving, {
      // Runs after the hook's awaited cache update, so the switches never flash the old values.
      onSuccess: () => {
        setDraft(null);
        toast.success(t.saved);
      },
      onError: (err) => toast.error(t.error, { description: apiErrorMessage(err, t.failedToSave) }),
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.title}</CardTitle>
        <CardDescription>{t.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ClientAccessSwitch
          title={t.private.title}
          explanation={t.private.explanation}
          advisorsDo={t.private.advisorsDo}
          checked={target.privateEnabled}
          changed={target.privateEnabled !== current.privateEnabled}
          disabled={update.isPending}
          onCheckedChange={(privateEnabled) => set({ privateEnabled })}
        />

        <ClientAccessSwitch
          title={t.team.title}
          explanation={t.team.explanation}
          advisorsDo={t.team.advisorsDo}
          checked={target.teamAccessEnabled}
          changed={target.teamAccessEnabled !== current.teamAccessEnabled}
          disabled={update.isPending}
          onCheckedChange={(teamAccessEnabled) => set({ teamAccessEnabled })}
        >
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/settings/teams"
              className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            >
              {noTeams ? t.createTeams : t.setUpTeams}
              <ArrowRight aria-hidden="true" className="h-3.5 w-3.5" />
            </Link>
            {teamCount !== undefined && (
              <Badge variant={noTeams && target.teamAccessEnabled ? "warning" : "secondary"}>
                {noTeams
                  ? t.noTeams
                  : teamCount === 1
                    ? t.teamCount.one
                    : t.teamCount.other(teamCount)}
              </Badge>
            )}
          </div>
        </ClientAccessSwitch>

        {target.privateEnabled && target.teamAccessEnabled && (
          <p className="flex items-start gap-2 text-sm text-muted-foreground">
            <Info aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
            {t.privateWins}
          </p>
        )}

        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <Info aria-hidden="true" className="mt-px h-3.5 w-3.5 shrink-0" />
          {t.alwaysAll}
        </p>

        <div id={impactId} aria-live="polite">
          {changing && <p className="sr-only">{t.unsaved}</p>}
          {changing && (
            <ClientAccessImpactPanel
              target={target}
              impact={impact}
              // Placeholder buckets belong to the previous target; never word them.
              isLoading={impactLoading || impactIsPrevious}
              isRetrying={impactFetching}
              onRetry={() => void refetchImpact()}
            />
          )}
        </div>

        {changing && (
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={() => setDraft(null)} disabled={update.isPending}>
              {t.cancel}
            </Button>
            <Button
              onClick={save}
              loading={update.isPending}
              disabled={impactLoading || impactIsPrevious}
            >
              {t.change}
            </Button>
          </div>
        )}
      </CardContent>
      <ConfirmationDialog />
    </Card>
  );
}
